import axios from "axios";
import { globalAction } from "redux-subspace";
import { ModalSize } from "../components/ui-library/CustomModal/CustomModal";
import {
  apiUrl,
  publicLanguage,
  sessionClaims,
  sessionLanguage,
  sessionName,
  sessionRole,
  sessionSites,
  sessionStudies,
  sessionToken,
  sessionUsername,
} from "../config";
import i18n from "../i18n";
import {
  DEFAULT_LOCALE,
  getPublicLocale,
  refreshAppLanguage,
} from "../i18n/helpers";
import {
  HttpCodes,
  modalHeaders,
  modalType,
  profiles,
  studiesIds,
  unauthorizedStudyStatus,
} from "./constants";
import { COOKIE_ACCEPTED_STORAGE } from "./cookieConsent";
import history from "./history";
import { getTranslatedText } from "./utils";

const LOGIN = "LOGIN";
const SET_SESSION_TOKEN = "SET_SESSION_TOKEN";
const SET_NEW_PASSWORD = "SET_NEW_PASSWORD";
const LOGOUT = "LOGOUT";
const PREVENT_NAVIGATION = "PREVENT_NAVIGATION";
const SET_ERROR_MSG = "SET_ERROR_MSG";
const OPEN_AUTH_MODAL = "OPEN_AUTH_MODAL";
const CLOSE_AUTH_MODAL = "CLOSE_AUTH_MODAL";
const SET_MODAL_TITLE = "SET_MODAL_TITLE";
const SET_STUDY_STATUS = "SET_STUDY_STATUS";
const SET_LOGIN_USERNAME = "SET_LOGIN_USERNAME";

const defaultState = {
  token: null,
  role: "",
  status: 0,
  error: "",
  preventNavigation: false,
  nonce: 0,
  studies: null,
  claims: [],
  content: {
    metadata: {},
  },
  authModalProps: {
    centered: true,
    show: true,
    current: "",
    title: "",
    size: ModalSize.small,
  },
  modalSubtitle: "",
  modalButtonText: null,
  passwordToken: null,
  acceptedTermsAndConditions: null,
  loginUsername: "",
};

export default function loginReducer(state = defaultState, action) {
  switch (action.type) {
    case LOGIN:
      return {
        ...state,
        token: action.data.token,
        role: action.data.role,
        status: action.data.status,
        error: action.data.message,
        nonce: state.nonce + 1,
        studies: action.data.studies,
        username: action.data.username,
        name: action.data.name,
        claims: action.data.claims,
        sites: action.data.sites,
        passwordToken: action.data.passwordToken,
        acceptedTermsAndConditions: action.data.acceptedTermsAndConditions,
      };
    case SET_NEW_PASSWORD:
      return {
        ...state,
        username: action.data.username,
        name: action.data.name,
        passwordToken: action.data.passwordToken,
        acceptedTermsAndConditions: action.data.acceptedTermsAndConditions,
      };
    case LOGOUT: {
      return defaultState;
    }
    case PREVENT_NAVIGATION: {
      return {
        ...state,
        preventNavigation: action.preventNavigation,
      };
    }
    case SET_SESSION_TOKEN:
      return {
        ...state,
        token: action.token,
        role: action.role,
        preventNavigation: action.token ? true : false,
        studies: action.studies,
        username: action.username,
        name: action.name,
        claims: action.claims,
        sites: action.sites,
      };
    case SET_STUDY_STATUS:
      return {
        ...state,
        studies: action.studies,
      };
    case SET_MODAL_TITLE:
      return {
        ...state,
        authModalProps: {
          ...state.authModalProps,
          title: action.title,
        },
      };
    case SET_ERROR_MSG:
      return {
        ...state,
        error: "",
      };
    case OPEN_AUTH_MODAL:
      return {
        ...state,
        authModalProps: {
          ...state.authModalProps,
          show: true,
          current: action.id,
          title: action.title,
          size: ModalSize.small,
        },
        content: {
          ...state.content,
          metadata: action.metadata,
        },
        modalSubtitle: action.modalSubtitle,
        modalButtonText: action.modalButtonText,
      };
    case CLOSE_AUTH_MODAL:
      return {
        ...state,
        authModalProps: {
          ...state.authModalProps,
          current: null,
          title: "",
        },
      };
    case SET_LOGIN_USERNAME:
      return {
        ...state,
        loginUsername: action.loginUsername,
      };
    default: {
      return state;
    }
  }
}

export const login = (user) => async (dispatch) => {
  try {
    const baseUrl = await apiUrl();
    const { data } = await axios.post(`${baseUrl}/api/account/login`, user);
    return handleLogin(data, dispatch);
  } catch (error) {
    if (error) {
      return Promise.reject(
        handleLoginError(error, error.response?.status || error.request?.status)
      );
    }
    return Promise.reject();
  }
};

export const fetchParticipantStudyStatus = () => (dispatch, getState) => {
  const studies = getAccountSessionStudies(getState());
  const encryptedRole = getAccountSessionRole(getState());
  const role = decrypt(encryptedRole);

  if (studies && studies.length > 0 && role === profiles.participant) {
    const params = {
      studyId: studies[0]?.studyId,
    };

    return axios
      .get(`/api/participant/contactinformation`, {
        params,
      })
      .then(({ data }) => {
        const studyId = studies[0]?.studyId;

        studies.forEach((study) => {
          if (study.studyId === studyId) {
            study.status = data.studyStatus;
          }
        });

        dispatch({ type: SET_STUDY_STATUS, studies });
        return Promise.resolve();
      })
      .catch((error) => {
        //sendNotification(error, ToastType.ERROR);
      });
  } else {
    return Promise.resolve();
  }
};

const handleLogin = (data, dispatch) => {
  if (data?.passwordToken) {
    dispatch(
      globalAction({
        type: SET_NEW_PASSWORD,
        data: {
          username: data.username,
          name: data.name,
          passwordToken: data.passwordToken,
          acceptedTermsAndConditions: data.acceptedTermsAndConditions,
        },
      })
    );
  } else {
    sessionStorage.setItem(sessionToken, data.token);
    sessionStorage.setItem(sessionRole, data.role);
    sessionStorage.setItem(sessionStudies, JSON.stringify(data?.studies || []));
    sessionStorage.setItem(sessionClaims, JSON.stringify(data?.claims || []));
    sessionStorage.setItem(sessionSites, JSON.stringify(data?.sites || []));
    sessionStorage.setItem(sessionUsername, data.username);
    sessionStorage.setItem(sessionName, data.name);
    sessionStorage.setItem(sessionLanguage, data.language || DEFAULT_LOCALE);

    refreshAppLanguage();

    dispatch(
      globalAction({
        type: LOGIN,
        data: {
          token: data.token,
          role: data.role,
          status: HttpCodes.OK,
          message: "",
          studies: data.studies,
          claims: data.claims,
          username: data.username,
          name: data.name,
          sites: data.sites || [],
          passwordToken: data.passwordToken,
          acceptedTermsAndConditions: data.acceptedTermsAndConditions,
        },
      })
    );
    dispatch(
      globalAction({ type: PREVENT_NAVIGATION, preventNavigation: true })
    );

    // Reload the app (needed for state management, for some reason) and navigate to the user's default route
    const decryptedRole = decrypt(data?.role);
    const defaultRoute = getUserDefaultRoute(decryptedRole);
    window.location.href = window.location.origin + defaultRoute;
  }

  const decryptedRole = decrypt(data?.role);
  const studyId = data?.studies ? data?.studies[0]?.studyId : null;
  return Promise.resolve({ ...data, decryptedRole, studyId });
};

const handleLoginError = (error, status) => {
  switch (status) {
    case HttpCodes.UNAUTHORIZED: {
      return i18n.t("errorText.auth.login.unauthorized");
    }
    case HttpCodes.FORBIDDEN: {
      return (
        getTranslatedText(error?.response?.data) ||
        i18n.t("errorText.auth.login.forbidden")
      );
    }
    case HttpCodes.NOT_FOUND:
    case HttpCodes.PRE_CONDITION_FAILED: {
      return i18n.t("appText.auth.account.incomplete");
    }
    case HttpCodes.FOUND: {
      return getTranslatedText(error?.response?.data);
    }
    case HttpCodes.INTERNAL_ERROR:
      return i18n.t("errorText.auth.login.internalError");
    case 0:
      return;
    default:
      return i18n.t("errorText.auth.login.internalError");
  }
};

/**
 * Returns a user landing or admin landing for any study
 * @param {*} roles
 * @param {*} studyid
 * @param {*} studyEnum
 * @param {*} studyId
 */
export const getUserDefaultRoute = (role) => {
  if (
    [
      profiles.contentAdmin,
      profiles.studyAdmin,
      profiles.pi,
      profiles.dietitian,
      profiles.siteCoordinator,
      profiles.sponsor,
      profiles.siteMonitor,
      profiles.medicalMonitor,
      profiles.dataManager,
    ].indexOf(role) >= 0
  ) {
    return "/admin";
  } else {
    switch (role) {
      case profiles.participant:
        return `/participant`;
      case profiles.itAdmin:
        return `/management`;
      default:
        return `/home`;
    }
  }
};

export const getClaims = (state, claim) => {
  return state?.loginReducer?.claims?.includes(claim);
};

export const logout =
  (refreshLanguage = true) =>
  (dispatch) => {
    axios
      .get("/api/account/logout?sessionExpired=false")
      .then((r) => {
        return r;
      })
      .catch((e) => {
        console.error(e);
        return e;
      })
      .finally(() => {
        sessionStorage.clear();
        sessionStorage.removeItem(sessionToken);
        sessionStorage.removeItem(sessionRole);
        sessionStorage.removeItem(sessionStudies);
        sessionStorage.removeItem(sessionClaims);
        sessionStorage.removeItem(sessionSites);
        sessionStorage.removeItem(sessionUsername);
        sessionStorage.removeItem(sessionName);
        sessionStorage.removeItem(sessionLanguage);

        const cookieAccepted =
          localStorage.getItem(COOKIE_ACCEPTED_STORAGE) || null;

        const publicLocale = getPublicLocale();

        dispatch({
          type: LOGOUT,
        });
        globalAction({
          type: LOGOUT,
        });

        localStorage.clear();

        sessionStorage.clear();
        localStorage.setItem(COOKIE_ACCEPTED_STORAGE, cookieAccepted);

        sessionStorage.setItem(sessionLanguage, publicLocale);
        localStorage.setItem(publicLanguage, publicLocale);

        if (refreshLanguage) refreshAppLanguage();

        history.push("/home");
      });
  };

export const sessionTimeout = () => (dispatch) => {
  axios
    .get("/api/account/logout?sessionExpired=true")
    .then((r) => {
      return r;
    })
    .catch((e) => {
      console.error(e);
      return e;
    })
    .finally(() => {
      const title = i18n.t("appText.sessionTimeOut.title");
      const message = i18n.t("appText.sessionTimeOut.message");
      const buttonText = i18n.t("appText.auth.login");

      sessionStorage.clear();
      sessionStorage.removeItem(sessionToken);
      sessionStorage.removeItem(sessionRole);
      sessionStorage.removeItem(sessionStudies);
      sessionStorage.removeItem(sessionClaims);
      sessionStorage.removeItem(sessionSites);
      sessionStorage.removeItem(sessionUsername);
      sessionStorage.removeItem(sessionName);
      sessionStorage.removeItem(sessionLanguage);

      const cookieAccepted =
        localStorage.getItem(COOKIE_ACCEPTED_STORAGE) || null;

      const publicLocale = getPublicLocale();

      dispatch({
        type: LOGOUT,
      });
      globalAction({
        type: LOGOUT,
      });

      localStorage.clear();

      sessionStorage.clear();
      localStorage.setItem(COOKIE_ACCEPTED_STORAGE, cookieAccepted);

      sessionStorage.setItem(sessionLanguage, publicLocale);
      localStorage.setItem(publicLanguage, publicLocale);

      history.push("/home");

      setTimeout(() => {
        dispatch(
          setOpenAuthModal(
            modalType.loggedOut,
            title,
            null,
            message,
            buttonText
          )
        );
      }, 1000);
    });
};

export const setModalTitle = (title) => {
  return (dispatch) => {
    dispatch(
      globalAction({
        type: SET_MODAL_TITLE,
        title,
      })
    );
  };
};

export const setLoginUsername = (loginUsername) => {
  return (dispatch) => {
    dispatch(
      globalAction({
        type: SET_LOGIN_USERNAME,
        loginUsername,
      })
    );
  };
};

export const setOpenAuthModal = (
  id,
  title,
  metadata = {},
  modalSubtitle = "",
  modalButtonText = null
) => {
  return (dispatch) => {
    dispatch(
      globalAction({
        type: "OPEN_AUTH_MODAL",
        id,
        title,
        metadata,
        modalSubtitle,
        modalButtonText,
      })
    );
  };
};

export const onAuthModalClose = () => {
  return (dispatch) => {
    dispatch(globalAction({ type: "CLOSE_AUTH_MODAL" }));
    return Promise.resolve();
  };
};

export const closeModalGoHome = () => {
  return (dispatch) => {
    dispatch(globalAction({ type: "CLOSE_AUTH_MODAL" }));
  };
};

export const closeModalLogout = () => {
  return (dispatch) => {
    dispatch(globalAction({ type: "CLOSE_AUTH_MODAL" }));
    dispatch(logout());
  };
};

export const goHome = () => (dispatch, getState) => {
  let r = getUserProfile(getState());
  try {
    history.push(getUserDefaultRoute(r));
  } catch (error) {
    return Promise.reject(error);
  }
  return Promise.resolve();
};

export const setPreventNavigation = (value) => (dispatch) => {
  dispatch({ type: PREVENT_NAVIGATION, preventNavigation: value });
  return Promise.resolve(PREVENT_NAVIGATION);
};

export const setSessionToken =
  (token, role, studies, username, name, claims, sites) => (dispatch) => {
    dispatch({
      type: SET_SESSION_TOKEN,
      token,
      role,
      studies,
      username,
      name,
      claims,
      sites,
    });
    return Promise.resolve(SET_SESSION_TOKEN);
  };

export const initializeSession = () => (dispatch) => {
  const token = sessionStorage.getItem(sessionToken) || null;
  let studies = [];
  let claims = [];
  let sites = [];
  if (token) {
    const role = sessionStorage.getItem(sessionRole) || null;
    const username = sessionStorage.getItem(sessionUsername) || null;
    const name = sessionStorage.getItem(sessionName) || null;
    try {
      studies = JSON.parse(sessionStorage.getItem(sessionStudies)) || [];
      claims = JSON.parse(sessionStorage.getItem(sessionClaims)) || [];
      sites = JSON.parse(sessionStorage.getItem(sessionSites)) || [];
    } catch (error) {
      refreshPublicLanguage();

      return Promise.reject(i18n.t("errorText.auth.invalid.localdata"));
    }

    return dispatch(
      setSessionToken(token, role, studies, username, name, claims, sites)
    ).catch(() => {
      refreshPublicLanguage();
      dispatch(logout());
      return Promise.reject(i18n.t("errorText.auth.invalid.localdata"));
    });
  }
  return Promise.resolve();
};

const refreshPublicLanguage = () => {
  const publicLocale = getPublicLocale();

  localStorage.clear();

  sessionStorage.clear();

  sessionStorage.setItem(sessionLanguage, publicLocale);
  localStorage.setItem(publicLanguage, publicLocale);
};

export const openLoginModal = () => (dispatch) =>
  dispatch(setOpenAuthModal(modalType.login, modalHeaders.login));

export const getAccountSessionToken = (state) => state?.loginReducer?.token;
export const getAccountSessionRole = (state) => state?.loginReducer?.role;
export const getAccountSessionStudies = (state) => state?.loginReducer?.studies;
export const getAccountUsername = (state) => state?.loginReducer?.username;
export const getAccountName = (state) => state?.loginReducer?.name;

export const decrypt = (encryptedInfo) => {
  let info = "";
  if (encryptedInfo) {
    const text = atob(encryptedInfo);
    const length = text.length;
    const bytes = new Uint8Array(length);
    for (let i = 0; i < length; i++) {
      bytes[i] = text.charCodeAt(i);
    }
    const decoder = new TextDecoder(); // default is utf-8
    info = JSON.parse(decoder.decode(bytes)).info;
  }
  return info;
};

export const getUserProfileUrl = (type) => {
  if (type === profiles.participant) {
    return "/participant";
  } else if (type === profiles.contentAdmin) {
    return "/content-admin";
  } else if (type === profiles.itAdmin) {
    return "/management";
  }
  return "/";
};

export const getUserProfileDescription = (type) => {
  if (type === profiles.participant) {
    return "Community";
  } else if (type === profiles.contentAdmin) {
    return "Community";
  } else if (type === profiles.itAdmin) {
    return "Users";
  }
  return "Profile";
};

export const getHomepageUrl = (type) => {
  if (type === profiles.participant) {
    return "/home";
  } else if (type === profiles.contentAdmin) {
    return "/content-admin";
  } else if (type === profiles.itAdmin) {
    return "/management";
  }
  return "/home";
};

export const getUserProfile = (state) => {
  const encryptedRole = getAccountSessionRole(state);
  const role = decrypt(encryptedRole);

  if (role) {
    const roles = role.split(",");
    if (roles.indexOf(profiles.contentAdmin) >= 0) {
      return profiles.contentAdmin;
    } else if (roles.indexOf(profiles.participant) >= 0) {
      return profiles.participant;
    } else if (roles.indexOf(profiles.itAdmin) >= 0) {
      return profiles.itAdmin;
    } else if (roles.indexOf(profiles.studyAdmin) >= 0) {
      return profiles.studyAdmin;
    } else if (roles.indexOf(profiles.pi) >= 0) {
      return profiles.pi;
    } else if (roles.indexOf(profiles.dietitian) >= 0) {
      return profiles.dietitian;
    } else if (roles.indexOf(profiles.siteCoordinator) >= 0) {
      return profiles.siteCoordinator;
    } else if (roles.indexOf(profiles.sponsor) >= 0) {
      return profiles.sponsor;
    } else if (roles.indexOf(profiles.siteMonitor) >= 0) {
      return profiles.siteMonitor;
    } else if (roles.indexOf(profiles.medicalMonitor) >= 0) {
      return profiles.medicalMonitor;
    } else if (roles.indexOf(profiles.dataManager) >= 0) {
      return profiles.dataManager;
    } else {
      return profiles.none;
    }
  }
};

export const getPreventNavigation = (state) => {
  return state?.loginReducer?.preventNavigation;
};

export const getStudyIds = (studies) => {
  let tmpStudies = [];
  if (studies) {
    studies.forEach((s) => {
      tmpStudies.push(s?.studyId?.toLowerCase());
    });
    return tmpStudies;
  }
  return [];
};

export const isStudyEnrolled = (study, studies, unauthorizedStudyStatus) => {
  let studyEnrolled = true;
  if (study) {
    const checkStudiesIds = [
      studiesIds.gsdiii.toLowerCase(),
      studiesIds.lcfaod401.toLowerCase(),
      studiesIds.lcfaod302.toLowerCase(),
    ];
    studies &&
      studies.forEach((s) => {
        if (study?.toLowerCase() === s?.studyId?.toLowerCase()) {
          if (
            checkStudiesIds.includes(s?.studyId.toLowerCase()) &&
            unauthorizedStudyStatus?.length > 0
          ) {
            const checkAuthorizedStatus = unauthorizedStudyStatus.find(
              (u) => u.id === s?.status
            );
            if (checkAuthorizedStatus) {
              studyEnrolled = false;
            }
          }
        }
      });
  }
  return studyEnrolled;
};

export const isUserAuthenticated = (state) => !!state?.loginReducer?.token;

export const resetErrorMsg = () => (dispatch) => {
  return dispatch({
    type: SET_ERROR_MSG,
  });
};

export const clearStudyStatus = () => (dispatch, getState) => {
  let studies = getAccountSessionStudies(getState());
  let study = { ...studies[0] };
  study.status = unauthorizedStudyStatus.lcfaod302[0].id;
  studies = [study];

  dispatch({
    type: SET_STUDY_STATUS,
    studies,
  });
};

export const mapStateToPropsLogin = (state) => {
  const {
    token,
    role: userRole,
    status,
    error,
    nonce,
    authModalProps,
    modalSubtitle,
    modalButtonText,
    claims,
    sites,
    passwordToken,
    acceptedTermsAndConditions,
    loginUsername,
    content,
  } = state.loginReducer;

  const isLogged = isUserAuthenticated(state);
  const userProfile = getUserProfile(state);
  const preventNavigation = getPreventNavigation(state);
  const studies = getAccountSessionStudies(state);
  const accountUserName = decrypt(getAccountUsername(state));
  const accountName = decrypt(getAccountName(state));
  const canViewTargetPanel = getClaims(state, "participant.target.canView");
  const canViewFavorites = getClaims(state, "favorites.canView");
  const canViewComments = getClaims(state, "comments.canView");
  const canViewNutrientChart = getClaims(
    state,
    "mealLog.nutrientChart.canView"
  );
  const canViewEntriesTable = getClaims(state, "mealLog.canView");
  const canViewUserGuide = getClaims(state, "site.userGuide.canView");

  return {
    token,
    isLogged,
    status,
    error,
    nonce,
    userRole,
    userProfile,
    preventNavigation,
    studies,
    accountUserName,
    accountName,
    authModalProps,
    modalSubtitle,
    modalButtonText,
    claims,
    sites,
    passwordToken,
    acceptedTermsAndConditions,
    canViewTargetPanel,
    canViewFavorites,
    canViewComments,
    canViewNutrientChart,
    canViewEntriesTable,
    canViewUserGuide,
    loginUsername,
    content,
  };
};

export const mapDispatchToPropsLogin = {
  login,
  logout,
  sessionTimeout,
  setPreventNavigation,
  resetErrorMsg,
  setOpenAuthModal,
  onAuthModalClose,
  closeModalGoHome,
  closeModalLogout,
  setModalTitle,
  setLoginUsername,
};
